import shortid from 'shortid'
import { buildFieldNameMap } from '../Wizard2/WizardActions'
import { getFieldsByType } from 'utilities'
import orderBy from 'lodash/orderBy'

const CATEGORIES = {
  accessibility: 'Accessibility',
  dataSafety: 'Data Safety',
}

export const SEVERITIES = {
  critical: { id: 6, label: 'Critical', name: 'critical' },
  moderate: { id: 5, label: 'Moderate', name: 'moderate' },
  warning: { id: 4, label: 'Warning', name: 'warning' },
  info: { id: 3, label: 'Informational', name: 'info' },
}

export const LOG_GROUPS = {
  0: {
    label: 'Accessibility',
  },
  1: {
    label: 'Field name is not unique',
  },
}

const SCREEN_READER_HINT =
  'This will make the field difficult for screen reader users to understand.'

const SAVED_FILING_HINT = 'or their values will not be preserved when saving a filing.'
const RECONNECT_NODES_HINT = 'Reconnect or delete these nodes or this logic will not operate.'

const MESSAGES = {
  fieldLabelMissing: `${SCREEN_READER_HINT} With a few exceptions, fields should always have a label even if it isn't shown to the user.`,
  fieldLabelMissingInFirstField: `It looks like you may be trying to use a section title as the label for this field.`,
  fieldNameMissing: `Fields must have a unique database field name ${SAVED_FILING_HINT}`,
  fieldNameNotUnique:
    'Field names must be unique or their values will not be preserved when saving a filing.',
  fieldNameNotUniqueWithConditions: `This field shares a field name with another field(s). Ensure that they are visible to different groups or that the logic governing these fields never allows them to display in the Wizard at the same time, even on different steps, ${SAVED_FILING_HINT}`,
  fieldDescriptionUsedAsLabel: `It looks like you may be trying to use a description as the label for this field. ${SCREEN_READER_HINT}`,
  referenceFieldIsDeleted: (attributeName) =>
    `The "${attributeName}" attribute references a field that has been deleted or is otherwise invalid. This will not allow the Wizard to load.`,
  stepTemplateIdNotUnique:
    'Step template IDs must be unique or the first template ID found will be used as the template step.',
  logicReferencesDeletedFields: `One or more logic nodes reference fields which have been deleted. ${RECONNECT_NODES_HINT}`,
  customRuleReferencesDeletedFields: label => `One or more logic nodes in the custom business rule, "${label}", reference fields which have been deleted. ${RECONNECT_NODES_HINT}`,
}

const UNLABELED_FIELD_TYPES = ['paragraph', 'repeater', 'UCCSearch']

export const getEntityType = (entity) => {
  if (entity.classType === 'Step') {
    return 'STEP'
  }
  if (entity.type === 'section') {
    return 'SECTION'
  }
  if (entity.parent.type.toUpperCase() === 'REPEATER') {
    return 'REPEATER'
  }
  if (entity.parent.type.toUpperCase() === 'OPTION') {
    return 'OPTION'
  }
  if (entity.parent.type.toUpperCase() === 'CHECKBOX') {
    return 'CHECKBOX'
  }
  if (!!entity.fieldId) {
    return 'FIELD'
  }
  return 'UNHANDLED'
}

export const buildEntityMap = (entity, state) => {
  const type = getEntityType(entity)
  let map
  switch (type) {
    case 'REPEATER':
    case 'OPTION':
    case 'CHECKBOX': {
      let stepId = state.wizard.sections[state.wizard.fields[entity.parent.id].parent.id].stepId
      if (!stepId) {
        stepId = Object.values(state.wizard.steps).find((step) =>
          step.sectionOrder.includes(state.wizard.fields[entity.parent.id].parent.id)
        ).stepId
      }
      return {
        type,
        parentFieldId: entity.parent.id,
        fieldId: entity.fieldId,
        sectionId: state.wizard.fields[entity.parent.id].parent.id,
        stepId,
      }
    }
    case 'FIELD':
      map = {
        type,
        fieldId: entity.fieldId,
        sectionId: entity.parent.id,
        stepId: state.wizard.sections[entity.parent.id].stepId,
      }
      if (!map.stepId) {
        map.stepId = state.wizard.stepOrder.find((stepId) =>
          state.wizard.steps[stepId].sectionOrder.includes(entity.parent.id)
        )
      }
      return map
    case 'SECTION':
      return {
        type,
        sectionId: entity.sectionId,
        stepId: entity.stepId,
      }
    case 'STEP':
      return {
        type,
        stepId: entity.stepId,
      }
    default:
      return {}
  }
}

export const getFieldsInOrder = ({ steps, stepOrder, sections, fields }) =>
  stepOrder.flatMap((stepId) =>
    steps[stepId].sectionOrder.flatMap((sectionId) =>
      sections[sectionId].fieldOrder.flatMap((fieldId) => fields[fieldId])
    )
  )

export const getFieldIsFirstInSection = ({ field, sections }) => {
  if (field.parent.type.toUpperCase() !== 'SECTION') return false
  return sections[field.parent.id].fieldOrder[0] === field.fieldId
}

export const fieldsWithoutLabels = ({ allFields, wizard: { sections } }, Logger) => {
  allFields.forEach((field) => {
    if (!field.label && !UNLABELED_FIELD_TYPES.includes(field.type)) {
      const fieldIsFirstInSection = getFieldIsFirstInSection({ field, sections })
      let warning = {
        title: `Field has no label or description`,
        category: CATEGORIES.accessibility,
        severity: SEVERITIES.warning,
        entity: field,
      }
      if (field.description) {
        warning.title = 'Description used as field label'
        warning.message = MESSAGES.fieldDescriptionUsedAsLabel
      } else if (fieldIsFirstInSection) {
        warning.title = 'Section title used as field label'
        warning.message = `${MESSAGES.fieldLabelMissingInFirstField} ${MESSAGES.fieldLabelMissing}`
      } else {
        warning.message = MESSAGES.fieldLabelMissing
        warning.severity = SEVERITIES.critical
      }
      Logger.log(warning)
    }
  })
}

export const fieldsWithoutNames = ({ allFields }, Logger) => {
  allFields.forEach((field) => {
    if (field.name === '') {
      Logger.log({
        title: 'Field has no database field name',
        category: CATEGORIES.dataSafety,
        severity: SEVERITIES.critical,
        entity: field,
        message: MESSAGES.fieldNameMissing,
      })
    }
  })
}

const hasNonMutuallyExclusiveConditions = (field1, field2) => {
  let field1ConditionParents = field1.conditions.map((c) => c.parentId)
  let field2ConditionParents = field2.conditions.map((c) => c.parentId)

  let sharesAllConditionParents =
    (field1.conditionsOperator === 'or'
      ? field1.conditions.every((c1) => field2ConditionParents.includes(c1.parentId))
      : true) &&
    (field2.conditionsOperator === 'or'
      ? field2.conditions.every((c2) => field1ConditionParents.includes(c2.parentId))
      : true)

  if (!sharesAllConditionParents) {
    return true
  }

  let allMutuallyExclusive = true

  for (let i = 0; i < field1.conditions.length; i++) {
    for (let k = 0; k < field2.conditions.length; k++) {
      const c1 = field1.conditions[i]
      const c2 = field2.conditions[k]
      if (c1.parentId === c2.parentId) {
        // Depends on the same field
        if (
          c1.value === c2.value &&
          (field1.conditionsOperator === 'or' || field2.conditionsOperator === 'or')
        ) {
          allMutuallyExclusive = false
        }
      }
    }
  }
  return !allMutuallyExclusive
}

export const fieldNamesNotUnique = ({ allFields, wizard: { fields, sections, mode } }, Logger) => {
  const fieldNameMap = buildFieldNameMap(
    allFields.map((f) => f.fieldId),
    fields
  )

  allFields.forEach((field) => {
    if (
      fieldNameMap[field.name] > 1 &&
      !field.disabled &&
      field.active &&
      !(mode !== 'amendment' && field.type === 'paragraph')
    ) {
      const otherFieldsWithName = Object.values(fields).filter((f) => {
        if (
          f.name === field.name &&
          !f.disabled &&
          f.active &&
          !(mode !== 'amendment' && f.type === 'paragraph')
        ) {
          if (f.fieldId === field.fieldId) return false
          if (field.parent.type === 'REPEATER') {
            if (f.parent.type === 'REPEATER') {
              return f.parent.id === field.parent.id
            }
            return true
          } else {
            const bothFieldsHaveCondtions = field.conditions && f.conditions
            if (bothFieldsHaveCondtions) {
              return hasNonMutuallyExclusiveConditions(field, f)
            } else {
              return true
            }
          }
        }
        return false
      })

      if (otherFieldsWithName.length > 0) {
        let log = {
          title: 'Field name is not unique',
          category: CATEGORIES.dataSafety,
          entity: field,
        }

        const fieldsWithName = [field, ...otherFieldsWithName]

        const numFieldsWithLogicOrConditions = fieldsWithName.filter(
          (f) => (f.conditions && f.conditions.length > 0) || Object.keys(f.logicNodes).length > 1
        ).length

        const visibleToGroups = fieldsWithName.map(f => sections[f.parent.id] && sections[f.parent.id].visibleTo)

        const numVisibleToGroups = new Set(visibleToGroups).size

        let uniqueGroups = fieldsWithName.length === numVisibleToGroups ? true : false;

        visibleToGroups.forEach(visibleTo => {
          if(visibleTo === "Public SOSStaff"){
            uniqueGroups = false;
          }
        })

        if (numFieldsWithLogicOrConditions >= fieldsWithName.length - 1 || uniqueGroups) {
          log.severity = SEVERITIES.warning
          log.message = MESSAGES.fieldNameNotUniqueWithConditions
        } else {
          log.severity = SEVERITIES.critical
          log.message = MESSAGES.fieldNameNotUnique
        }
        Logger.log(log)
      }
    }
  })
}

const templateIdsNotUnique = ({ wizard: { steps } }, Logger) => {
  const stepsArray = Object.values(steps)

  stepsArray.forEach((step) => {
    const templateIdIsNotUnique =
      step.templateId !== '' &&
      stepsArray.some((s) => s.stepId !== step.stepId && s.templateId === step.templateId)
    if (templateIdIsNotUnique) {
      Logger.log({
        title: 'Step template ID is not unique',
        category: CATEGORIES.dataSafety,
        severity: SEVERITIES.critical,
        message: MESSAGES.stepTemplateIdNotUnique,
        entity: step,
      })
    }
  })
}

const serverSelectOverridesExist = ({ wizard: { fields } }, Logger) => {
  const serverControls = getFieldsByType('serverSelect', fields)

  if (serverControls) {
    serverControls.forEach((field) => {
      if (field.optionsSrcOverride) {
        if (!fields[field.optionsSrcOverride]) {
          Logger.log({
            title: 'Options source override is not valid',
            category: CATEGORIES.dataSafety,
            severity: SEVERITIES.critical,
            message: MESSAGES.referenceFieldIsDeleted('Override options source with this field'),
            entity: field,
          })
        }
      }
      if (field.subOptionsSrcOverride) {
        if (!fields[field.subOptionsSrcOverride]) {
          Logger.log({
            title: 'Sub-options source override is not valid',
            category: CATEGORIES.dataSafety,
            severity: SEVERITIES.critical,
            message: MESSAGES.referenceFieldIsDeleted(
              'Override sub-options source with this field'
            ),
            entity: field,
          })
        }
      }
    })
  }
}

const fieldIdFlumeKeys = [
  'fieldId',
  'optionFieldId',
  'checkboxFieldId',
  'textboxFieldId',
  'uploadAndSubmitFieldId',
  'uploadFieldId',
  'dateFieldId',
  'entityNameFieldId',
  'nameQualifiedOrgFieldId',
  'repeaterFieldId',
  'formationLocaleFieldId',
]

const getFieldIdsFromNodeData = (data) => {
  const fieldIds = []
  Object.entries(data).forEach(([key, value]) => {
    if (fieldIdFlumeKeys.includes(key)) {
      fieldIds.push(value[key])
    }
  })
  return fieldIds
}

const nodesWithStaleFieldIds = ({ allFields, wizard: { fields } }, Logger) => {
  allFields.forEach((field) => {
    Object.values(field.logicNodes).forEach((node) => {
      const fieldIds = getFieldIdsFromNodeData(node.inputData)
      const hasMissingFieldIds = !!fieldIds.find((fieldId) => {
        return !fields[fieldId]
      })
      if (hasMissingFieldIds) {
        Logger.log({
          title: 'Logic references deleted fields',
          category: CATEGORIES.dataSafety,
          message: MESSAGES.logicReferencesDeletedFields,
          severity: SEVERITIES.critical,
          entity: field,
        })
      }
    })
    field.businessRules.forEach((rule) => {
      if (rule.fn === 'customBusinessRules') {
        rule.props.rules.forEach((rule) => {
          const fieldIds = Object.values(rule.logicNodes).flatMap((node) => getFieldIdsFromNodeData(node.inputData))
          const hasMissingFieldIds = !!fieldIds.find((fieldId) => {
            return !fields[fieldId]
          })
          if (hasMissingFieldIds) {
            Logger.log({
              title: 'Custom business rules reference deleted fields',
              category: CATEGORIES.dataSafety,
              message: MESSAGES.customRuleReferencesDeletedFields(rule.label),
              severity: SEVERITIES.critical,
              entity: field,
            })
          }
        })
      }
    })
  })
}

export class Auditor {
  constructor(designer) {
    const { stepOrder, steps, sections, fields } = designer.wizard
    this.state = {
      designer: designer,
      wizard: designer.wizard,
      orderedFields: getFieldsInOrder({ stepOrder, steps, sections, fields }),
      allFields: Object.values(fields),
    }
    this.logs = []
  }
  log = ({ title, message, category, severity, entity }) => {
    this.logs.push({
      id: shortid.generate(),
      title,
      message,
      category,
      severity: severity.id,
      entityMap: buildEntityMap(entity, this.state),
    })
  }
  collect = (func) => {
    func(this.state, { log: this.log })
  }
  startAudit = () => {
    this.collect(fieldsWithoutLabels)
    this.collect(fieldsWithoutNames)
    this.collect(fieldNamesNotUnique)
    this.collect(serverSelectOverridesExist)
    this.collect(templateIdsNotUnique)
    this.collect(nodesWithStaleFieldIds)
    this.logs = orderBy(this.logs, 'severity', ['desc'])
  }
}
